import React from 'react';
import '../styles/legal.css';

const AcceptableUse = () => {
  return (
    <div className="legal-page">
      <div className="legal-container">
        <h1 className="legal-title">ADGRO ACCEPTABLE USE POLICY</h1>

        <div className="legal-meta">
          <p><strong>Effective Date:</strong> January 18, 2026</p>
        </div>

        <p className="legal-intro">
          This Acceptable Use Policy ("AUP") is incorporated into and forms part of the Adgro Terms of Service. This AUP describes the permitted and prohibited uses of the Adgro platform and services. Adgro is a product developed and operated by Rhombus Labs Inc.
        </p>
        <p>
          By using the Service, you agree to comply with this AUP. Rhombus Labs may update this AUP from time to time, and continued use of the Service constitutes acceptance of any changes.
        </p>

        <section className="legal-section">
          <h2>1. PERMITTED USE</h2>
          <p>The Adgro Service is intended solely for:</p>
          <ul>
            <li>Retailers, grocery stores, and legitimate businesses</li>
            <li>Lawful advertising and marketing purposes</li>
            <li>Compliant digital advertising campaigns</li>
            <li>Lawful business activities</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>2. PROHIBITED CONDUCT</h2>
          <p>You may not use the Service to:</p>

          <h3>2.1 Illegal Activities</h3>
          <ul>
            <li>Violate any applicable federal, provincial, or local law or regulation</li>
            <li>Facilitate illegal activities or promote illegal products or services</li>
            <li>Transmit content that violates criminal laws</li>
            <li>Violate privacy laws, including PIPEDA or provincial privacy legislation</li>
          </ul>

          <h3>2.2 Deceptive Advertising</h3>
          <ul>
            <li>Create false, misleading, or deceptive advertisements</li>
            <li>Make unsubstantiated claims about products or services</li>
            <li>Use bait-and-switch tactics or other deceptive practices</li>
            <li>Misrepresent pricing, availability, or product characteristics</li>
            <li>Violate the Competition Act or advertising standards</li>
          </ul>

          <h3>2.3 Spam and Unsolicited Communications</h3>
          <ul>
            <li>Send unsolicited commercial messages (spam)</li>
            <li>Violate Canada's Anti-Spam Legislation (CASL)</li>
            <li>Target advertisements without proper consent</li>
            <li>Use purchased or harvested contact lists without consent</li>
          </ul>

          <h3>2.4 Harmful Content</h3>
          <ul>
            <li>Transmit content that is defamatory, libelous, or slanderous</li>
            <li>Transmit content that harasses, threatens, or abuses others</li>
            <li>Transmit content that promotes violence or hatred</li>
            <li>Transmit content that is obscene or sexually explicit</li>
            <li>Transmit content that exploits or harms minors</li>
          </ul>

          <h3>2.5 Prohibited Products and Services</h3>
          <p>Advertising for the following is prohibited:</p>
          <ul>
            <li>Illegal drugs or controlled substances</li>
            <li>Weapons, ammunition, or explosives (unless legally permitted)</li>
            <li>Counterfeit goods or trademark-infringing products</li>
            <li>Tobacco products (subject to advertising restrictions)</li>
            <li>Gambling services (unless properly licensed)</li>
            <li>Any products or services prohibited by applicable law</li>
          </ul>

          <h3>2.6 Technical Abuse</h3>
          <ul>
            <li>Transmit malware, viruses, trojans, or other malicious code</li>
            <li>Attempt to gain unauthorized access to systems, networks, or accounts</li>
            <li>Interfere with or disrupt the Service or servers</li>
            <li>Probe, scan, or test the vulnerability of any system</li>
            <li>Circumvent authentication or security measures</li>
            <li>Reverse engineer, decompile, or disassemble the Service</li>
            <li>Engage in click fraud or ad manipulation</li>
          </ul>

          <h3>2.7 Identity and Representation</h3>
          <ul>
            <li>Misrepresent your identity, affiliation, or the origin of advertisements</li>
            <li>Impersonate any person, business, or entity</li>
            <li>Use misleading sender identification</li>
            <li>Engage in phishing or fraudulent schemes</li>
          </ul>

          <h3>2.8 Intellectual Property Violations</h3>
          <ul>
            <li>Infringe any copyright, trademark, patent, or other intellectual property right</li>
            <li>Use content you do not have the right to use in advertisements</li>
            <li>Misuse Adgro's trademarks, logos, or branding</li>
          </ul>

          <h3>2.9 Unauthorized Commercial Use</h3>
          <ul>
            <li>Resell, sublicense, or commercially exploit the Service without authorization</li>
            <li>Use the Service on behalf of unauthorized third parties</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>3. AI USE RESTRICTIONS</h2>
          <p>When using AI-assisted features of the Service, you may not:</p>
          <ul>
            <li>Use AI outputs without human review for critical advertising decisions</li>
            <li>Misrepresent AI-generated content as human-generated</li>
            <li>Attempt to manipulate, deceive, or abuse AI systems</li>
            <li>Use AI to generate content that violates this AUP</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>4. YOUR RESPONSIBILITIES</h2>
          <p>You are responsible for:</p>
          <ul>
            <li>All activities that occur under your account</li>
            <li>Ensuring all authorized users comply with this AUP</li>
            <li>Maintaining the security and confidentiality of your account credentials</li>
            <li>Promptly notifying Adgro of any unauthorized use of your account</li>
            <li>All advertising content created through your account</li>
            <li>Obtaining all necessary consents for advertising and tracking</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>5. MONITORING AND ENFORCEMENT</h2>

          <h3>5.1 Monitoring</h3>
          <p>Adgro may (but is not obligated to) monitor use of the Service to ensure compliance with this AUP. Adgro may review advertising content and campaign settings.</p>

          <h3>5.2 Enforcement Actions</h3>
          <p>If Adgro determines, in its sole discretion, that you have violated this AUP, we may take any of the following actions:</p>
          <ul>
            <li>Issue a warning</li>
            <li>Remove or disable prohibited advertising content</li>
            <li>Suspend access to some or all of the Service</li>
            <li>Terminate your account</li>
            <li>Report the violation to law enforcement or regulatory authorities</li>
            <li>Pursue any other legal remedies available</li>
          </ul>

          <h3>5.3 Immediate Suspension</h3>
          <p>Adgro may suspend your access immediately and without notice if: (a) there is an imminent threat to the Service, other users, or third parties; (b) required by law or by our advertising partners; or (c) you materially breach this AUP.</p>
        </section>

        <section className="legal-section">
          <h2>6. REPORTING VIOLATIONS</h2>
          <p>If you become aware of any violation of this AUP, please report it to:</p>
          <div className="legal-contact">
            <p>Email: <a href="mailto:abuse@adgro.ca">abuse@adgro.ca</a></p>
          </div>
          <p>Please provide as much detail as possible, including evidence of the violation.</p>
        </section>

        <section className="legal-section">
          <h2>7. INDEMNIFICATION</h2>
          <p>You agree to indemnify and hold harmless Adgro from any claims, damages, losses, or expenses arising from your violation of this AUP or your use of the Service in violation of any law or third-party rights.</p>
        </section>

        <section className="legal-section">
          <h2>8. CHANGES TO THIS POLICY</h2>
          <p>Adgro may update this AUP from time to time. We will notify you of material changes by posting the updated policy on our website or by email. Continued use of the Service after changes constitutes acceptance.</p>
        </section>

        <section className="legal-section">
          <h2>9. CONTACT</h2>
          <p>For questions about this AUP, please contact:</p>
          <div className="legal-contact">
            <p><strong>Rhombus Labs Inc.</strong></p>
            <p>Email: <a href="mailto:support@adgro.ca">support@adgro.ca</a></p>
          </div>
        </section>
      </div>
    </div>
  );
};

export default AcceptableUse;
